import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Badge, Alert, Row, Col } from 'react-bootstrap';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, collection, addDoc, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';

// Coerce a Firestore Timestamp / Date / ISO string / ms number to a JS Date (or
// null) so a non-Timestamp value never render-crashes the history table (#48).
const toJsDate = (val) => {
	if (!val) return null;
	if (typeof val.toDate === 'function') return val.toDate();
	if (val instanceof Date) return val;
	const d = new Date(val);
	return isNaN(d) ? null : d;
};
const fmtDate = (val) => { const d = toJsDate(val); return d ? d.toLocaleDateString() : '—'; };
const money = (val) => { const n = Number(val); return isNaN(n) ? '0.00' : n.toFixed(2); };

const PaymentHistory = ({ studentId, studentName, isEmployeeView = true }) => {
	const [student, setStudent] = useState(null);
	const [payments, setPayments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	const [showAddPayment, setShowAddPayment] = useState(false);
	const [showAddCharge, setShowAddCharge] = useState(false);

	// Payment form state
	const [paymentForm, setPaymentForm] = useState({
		amount: '',
		method: 'cash',
		notes: '',
		date: new Date().toISOString().split('T')[0],
	});

	// Charge form state
	const [chargeForm, setChargeForm] = useState({
		amount: '',
		description: '',
		date: new Date().toISOString().split('T')[0],
	});

	useEffect(() => {
		fetchStudentAndPayments();
	}, [studentId]);

	const fetchStudentAndPayments = async () => {
		setLoadError(null);
		try {
			// Fetch student data
			const studentDoc = await getDoc(doc(db, 'students', studentId));
			if (studentDoc.exists()) {
				setStudent({ id: studentDoc.id, ...studentDoc.data() });
			}

			// Fetch payment history
			const paymentsQuery = query(
				collection(db, 'payments'),
				where('studentId', '==', studentId),
				orderBy('date', 'desc')
			);
			const paymentsSnapshot = await getDocs(paymentsQuery);
			const paymentsData = paymentsSnapshot.docs.map(doc => ({
				id: doc.id,
				...doc.data()
			}));
			setPayments(paymentsData);
		} catch (error) {
			// Surface the failure instead of swallowing it into an empty "no
			// history" state — before the composite index existed this query threw
			// failed-precondition for everyone, silently showing $0/empty books (#18).
			console.error('Error fetching payment data:', error);
			setLoadError("We couldn't load this student's payment history just now. Please refresh in a moment.");
		} finally {
			setLoading(false);
		}
	};

	const handleAddPayment = async () => {
		try {
			const amount = parseFloat(paymentForm.amount);
			if (isNaN(amount) || amount <= 0) {
				alert('Please enter a valid payment amount');
				return;
			}

			// Add payment record
			await addDoc(collection(db, 'payments'), {
				studentId,
				studentName,
				type: 'payment',
				amount: amount,
				method: paymentForm.method,
				notes: paymentForm.notes,
				date: Timestamp.fromDate(new Date(paymentForm.date)),
				createdAt: Timestamp.now(),
			});

			// Update student balance
			const currentBalance = student?.accountBalance || 0;
			const newBalance = currentBalance + amount;
			await updateDoc(doc(db, 'students', studentId), {
				accountBalance: newBalance,
				lastPaymentDate: Timestamp.now(),
				lastPaymentAmount: amount,
			});

			// Reset form and refresh
			setPaymentForm({
				amount: '',
				method: 'cash',
				notes: '',
				date: new Date().toISOString().split('T')[0],
			});
			setShowAddPayment(false);
			fetchStudentAndPayments();
		} catch (error) {
			console.error('Error adding payment:', error);
			alert('Failed to add payment. Please try again.');
		}
	};

	const handleAddCharge = async () => {
		try {
			const amount = parseFloat(chargeForm.amount);
			if (isNaN(amount) || amount <= 0) {
				alert('Please enter a valid charge amount');
				return;
			}

			// Add charge record
			await addDoc(collection(db, 'payments'), {
				studentId,
				studentName,
				type: 'charge',
				amount: -amount, // Negative for charges
				description: chargeForm.description,
				date: Timestamp.fromDate(new Date(chargeForm.date)),
				createdAt: Timestamp.now(),
			});

			// Update student balance (subtract charge)
			const currentBalance = student?.accountBalance || 0;
			const newBalance = currentBalance - amount;
			await updateDoc(doc(db, 'students', studentId), {
				accountBalance: newBalance,
			});

			// Reset form and refresh
			setChargeForm({
				amount: '',
				description: '',
				date: new Date().toISOString().split('T')[0],
			});
			setShowAddCharge(false);
			fetchStudentAndPayments();
		} catch (error) {
			console.error('Error adding charge:', error);
			alert('Failed to add charge. Please try again.');
		}
	};

	const getBalanceBadge = (balance) => {
		if (balance === 0) {
			return <Badge bg='success'>Paid in Full</Badge>;
		} else if (balance > 0) {
			return <Badge bg='info'>Credit: ${balance.toFixed(2)}</Badge>;
		} else {
			return <Badge bg='danger'>Owes: ${Math.abs(balance).toFixed(2)}</Badge>;
		}
	};

	if (loading) {
		return <div className='text-center py-4'>Loading payment history...</div>;
	}

	const accountBalance = student?.accountBalance || 0;

	return (
		<>
			<Card className='shadow-sm mb-4'>
				<Card.Header style={{ backgroundColor: '#384971', color: '#fff' }}>
					<Row>
						<Col>
							<h5 className='mb-0'>
								<i className='fa-sharp fa-solid fa-credit-card me-2'></i>
								Account Balance
							</h5>
						</Col>
						<Col className='text-end'>
							{getBalanceBadge(accountBalance)}
						</Col>
					</Row>
				</Card.Header>
				<Card.Body>
					<Row className='mb-3'>
						<Col md={6}>
							<h3 className={accountBalance < 0 ? 'text-danger' : accountBalance > 0 ? 'text-info' : 'text-success'}>
								${Math.abs(accountBalance).toFixed(2)}
								{accountBalance < 0 && <small className='ms-2'>(Owed)</small>}
								{accountBalance > 0 && <small className='ms-2'>(Credit)</small>}
							</h3>
						</Col>
						<Col md={6}>
							<div className='text-muted small'>
								{student?.lastPaymentDate && (
									<>
										Last Payment: {fmtDate(student.lastPaymentDate)}
										<br />
										Amount: ${money(student.lastPaymentAmount)}
									</>
								)}
							</div>
						</Col>
					</Row>

					{isEmployeeView && (
						<div className='d-flex gap-2'>
							<Button variant='success' size='sm' onClick={() => setShowAddPayment(true)}>
								<i className='fa-sharp fa-solid fa-plus me-1'></i>
								Record Payment
							</Button>
							<Button variant='warning' size='sm' onClick={() => setShowAddCharge(true)}>
								<i className='fa-sharp fa-solid fa-plus me-1'></i>
								Add Charge
							</Button>
						</div>
					)}

					{accountBalance < 0 && (
						<Alert variant='warning' className='mt-3 mb-0'>
							<i className='fa-sharp fa-solid fa-exclamation-triangle me-2'></i>
							<strong>Outstanding Balance:</strong> This student has an unpaid balance of ${Math.abs(accountBalance).toFixed(2)}
						</Alert>
					)}
				</Card.Body>
			</Card>

			<Card className='shadow-sm'>
				<Card.Header style={{ backgroundColor: '#384971', color: '#fff' }}>
					<h5 className='mb-0'>
						<i className='fa-sharp fa-solid fa-history me-2'></i>
						Payment History ({payments.length})
					</h5>
				</Card.Header>
				<Card.Body>
					{loadError ? (
						<Alert variant='warning' className='mb-0'>
							<i className='fa-sharp fa-solid fa-triangle-exclamation me-2'></i>
							{loadError}
						</Alert>
					) : payments.length === 0 ? (
						<p className='text-muted text-center py-4'>No payment history yet</p>
					) : (
						<Table responsive hover>
							<thead>
								<tr>
									<th>Date</th>
									<th>Type</th>
									<th>Description</th>
									<th className='text-end'>Amount</th>
								</tr>
							</thead>
							<tbody>
								{payments.map(payment => (
									<tr key={payment.id}>
										<td>{fmtDate(payment.date)}</td>
										<td>
											{payment.type === 'payment' ? (
												<Badge bg='success'>
													<i className='fa-sharp fa-solid fa-arrow-down me-1'></i>
													Payment
												</Badge>
											) : (
												<Badge bg='warning' text='dark'>
													<i className='fa-sharp fa-solid fa-arrow-up me-1'></i>
													Charge
												</Badge>
											)}
										</td>
										<td>
											{payment.type === 'payment' ? (
												<>
													<span className='text-capitalize'>{payment.method}</span>
													{payment.notes && (
														<><br /><small className='text-muted'>{payment.notes}</small></>
													)}
												</>
											) : (
												payment.description || 'Lesson charge'
											)}
										</td>
										<td className={`text-end ${payment.amount >= 0 ? 'text-success' : 'text-danger'}`}>
											<strong>
												{payment.amount >= 0 ? '+' : ''}${money(payment.amount)}
											</strong>
										</td>
									</tr>
								))}
							</tbody>
						</Table>
					)}
				</Card.Body>
			</Card>

			{/* Add Payment Modal */}
			<Modal show={showAddPayment} onHide={() => setShowAddPayment(false)}>
				<Modal.Header closeButton>
					<Modal.Title>Record Payment</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Form>
						<Form.Group className='mb-3'>
							<Form.Label>Payment Amount</Form.Label>
							<Form.Control
								type='number'
								step='0.01'
								placeholder='0.00'
								value={paymentForm.amount}
								onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
							/>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Payment Method</Form.Label>
							<Form.Select
								value={paymentForm.method}
								onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
							>
								<option value='cash'>Cash</option>
								<option value='check'>Check</option>
								<option value='credit-card'>Credit Card</option>
								<option value='debit-card'>Debit Card</option>
								<option value='venmo'>Venmo</option>
								<option value='paypal'>PayPal</option>
								<option value='zelle'>Zelle</option>
								<option value='other'>Other</option>
							</Form.Select>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Payment Date</Form.Label>
							<Form.Control
								type='date'
								value={paymentForm.date}
								onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
							/>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Notes (Optional)</Form.Label>
							<Form.Control
								as='textarea'
								rows={2}
								placeholder='Add any notes about this payment...'
								value={paymentForm.notes}
								onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
							/>
						</Form.Group>
					</Form>
				</Modal.Body>
				<Modal.Footer>
					<Button variant='secondary' onClick={() => setShowAddPayment(false)}>
						Cancel
					</Button>
					<Button variant='success' onClick={handleAddPayment}>
						Record Payment
					</Button>
				</Modal.Footer>
			</Modal>

			{/* Add Charge Modal */}
			<Modal show={showAddCharge} onHide={() => setShowAddCharge(false)}>
				<Modal.Header closeButton>
					<Modal.Title>Add Charge</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Form>
						<Form.Group className='mb-3'>
							<Form.Label>Charge Amount</Form.Label>
							<Form.Control
								type='number'
								step='0.01'
								placeholder='0.00'
								value={chargeForm.amount}
								onChange={(e) => setChargeForm({ ...chargeForm, amount: e.target.value })}
							/>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Description</Form.Label>
							<Form.Control
								type='text'
								placeholder='e.g., Monthly lesson fee, Materials, etc.'
								value={chargeForm.description}
								onChange={(e) => setChargeForm({ ...chargeForm, description: e.target.value })}
							/>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Charge Date</Form.Label>
							<Form.Control
								type='date'
								value={chargeForm.date}
								onChange={(e) => setChargeForm({ ...chargeForm, date: e.target.value })}
							/>
						</Form.Group>
					</Form>
				</Modal.Body>
				<Modal.Footer>
					<Button variant='secondary' onClick={() => setShowAddCharge(false)}>
						Cancel
					</Button>
					<Button variant='warning' onClick={handleAddCharge}>
						Add Charge
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default PaymentHistory;
